import React, { useState, useEffect } from "react";
import Sidebar from "../components/Sidebar";
import Header from "../components/Header";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, Legend } from 'recharts';
import { TrendingUp, Users, Activity, BarChart2 } from "lucide-react";
import { API_BASE_URL } from "../config";

export default function Analytics() {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [revenueData, setRevenueData] = useState([]);
  const [userStats, setUserStats] = useState([]);
  const [summaryStats, setSummaryStats] = useState([
    { title: "Total Users", value: "...", trend: "...", icon: TrendingUp, color: "text-blue-600", bg: "bg-blue-50" },
    { title: "Total Subscriptions", value: "...", trend: "...", icon: Users, color: "text-indigo-600", bg: "bg-indigo-50" }
  ]);

  useEffect(() => {
    fetch(`${API_BASE_URL}/api/admin/analytics`)
      .then(res => {
        if (!res.ok) throw new Error("Failed to fetch analytics data");
        return res.json();
      })
      .then(result => {
        if (result.status && result.data) {
          setRevenueData(result.data.revenueData || []);
          setUserStats(result.data.userStats || []);

          if (result.data.summaryStats) {
            // Map the icons and colors correctly from API payload
            const mappedSummary = result.data.summaryStats.map((stat, i) => {
              const icons = [TrendingUp, Users, Activity, BarChart2];
              const colors = ["text-blue-600", "text-indigo-600", "text-emerald-600", "text-purple-600"];
              const bgs = ["bg-blue-50", "bg-indigo-50", "bg-emerald-50", "bg-purple-50"];
              return {
                title: stat.title,
                value: stat.value,
                trend: stat.trend,
                icon: icons[i] || TrendingUp,
                color: colors[i] || "text-blue-600",
                bg: bgs[i] || "bg-blue-50"
              };
            });
            setSummaryStats(mappedSummary);
          }
        }
        setLoading(false);
      })
      .catch(err => {
        console.error("Analytics fetch error:", err);
        setError(err.message);
        setLoading(false);
      });
  }, []);

  return (
    <div className="bg-[#f8fafc] dark:bg-slate-900 min-h-screen text-slate-800 dark:text-slate-100 font-sans selection:bg-blue-100 dark:selection:bg-blue-900 relative overflow-hidden">

      {/* Ambient background removed for sleek look */}

      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />

      <div className="flex-1 lg:ml-64 flex flex-col min-h-screen relative z-10 w-full lg:w-[calc(100%-16rem)]">
        <Header 
          onMenuClick={() => setIsSidebarOpen(true)} 
          title="Analytics Overview" 
          subtitle="Monitor your platform's performance and user engagement metrics."
        />

        <div className="p-4 sm:p-8 flex-1 flex flex-col w-full">
          {/* Header Section */}
          <div className="mb-6 sm:mb-8">
            
            
          </div>

          {error && (
            <div className="mb-6 p-4 bg-rose-50 dark:bg-rose-900/30 border border-rose-100 dark:border-rose-800 rounded-xl text-rose-600 dark:text-rose-400 text-sm font-medium">
              Error fetching analytics data: {error}
            </div>
          )}

          {loading ? (
            <div className="w-full flex-1 flex justify-center items-center h-64 text-slate-500">
              Loading analytics data...
            </div>
          ) : (
            <>

              {/* Quick Stats Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-4 sm:gap-6 mb-8">
                {summaryStats.map((stat, index) => (
                  <div key={index} className="bg-white dark:bg-slate-800 p-6 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 hover:shadow-md transition-all group">
                    <div className="flex justify-between items-start mb-4">
                      <div className={`p-3 rounded-xl ${stat.bg} ${stat.color} group-hover:scale-110 transition-transform`}>
                        <stat.icon size={24} />
                      </div>
                      <span className={`text-sm font-semibold px-2 py-1 rounded-full ${stat.trend.startsWith('+') ? 'text-emerald-600 bg-emerald-50' : 'text-rose-600 bg-rose-50'}`}>
                        {stat.trend}
                      </span>
                    </div>
                    <div>
                      <h3 className="text-slate-500 dark:text-slate-400 text-sm font-medium mb-1">{stat.title}</h3>
                      <p className="text-2xl font-bold text-slate-800 dark:text-slate-100">{stat.value}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Charts Area */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">

                {/* Main Area Chart */}
                <div className="lg:col-span-2 bg-white dark:bg-slate-800 p-6 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700">
                  <h3 className="text-lg font-bold text-slate-800 dark:text-slate-100 mb-6">Users vs Subscriptions (Weekly)</h3>
                  <div className="h-[300px] w-full">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={revenueData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                        <defs>
                          <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#2563eb" stopOpacity={0.3} />
                            <stop offset="95%" stopColor="#2563eb" stopOpacity={0} />
                          </linearGradient>
                          <linearGradient id="colorVisitors" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.3} />
                            <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                        <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: '#64748b', fontSize: 12 }} dy={10} />
                        <YAxis axisLine={false} tickLine={false} tick={{ fill: '#64748b', fontSize: 12 }} />
                        <Tooltip
                          contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)' }}
                          cursor={{ stroke: '#cbd5e1', strokeWidth: 1, strokeDasharray: '4 4' }}
                        />
                        <Area type="monotone" dataKey="users" name="Users Created" stroke="#8b5cf6" strokeWidth={3} fillOpacity={1} fill="url(#colorVisitors)" />
                        <Area type="monotone" dataKey="subscriptions" name="Subscriptions Created" stroke="#2563eb" strokeWidth={3} fillOpacity={1} fill="url(#colorRevenue)" />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                {/* Side Bar Chart */}
                <div className="bg-white dark:bg-slate-800 p-6 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700">
                  <h3 className="text-lg font-bold text-slate-800 dark:text-slate-100 mb-6">User Acquisition</h3>
                  <div className="h-[300px] w-full">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={userStats} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                        <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: '#64748b', fontSize: 12 }} dy={10} />
                        <YAxis axisLine={false} tickLine={false} tick={{ fill: '#64748b', fontSize: 12 }} />
                        <Tooltip
                          contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)' }}
                          cursor={{ fill: '#f1f5f9' }}
                        />
                        <Legend iconType="circle" wrapperStyle={{ fontSize: '12px', paddingTop: '20px' }} />
                        <Bar dataKey="active" name="Active Users" stackId="a" fill="#3b82f6" radius={[0, 0, 4, 4]} />
                        <Bar dataKey="new" name="New Users" stackId="a" fill="#10b981" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>

              </div>
            </>
          )}

        </div>
      </div>
    </div>
  );
}
